import { Avatar, Box, Group, ScrollArea, SegmentedControl, Text, ThemeIcon, UnstyledButton, Space } from '@mantine/core'
import { useAuthStore } from '../stores/auth'
import classes from './Navigation.module.css'
import linkClasses from './NavBarLinksGroup.module.css'
import { TbLogout2 } from 'react-icons/tb'
import { useState, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { LinksGroup } from './NavBarLinksGroup'
import { useTranslation } from 'react-i18next'

import { filterNavigationTabs, navigationTabs } from './Navigation.shared'

export type { NavigationTab, NavigationTabs } from './Navigation.shared'
export { navigationTabs } from './Navigation.shared'

type NavSection = 'system' | 'users'

export function Navigation() {
  const { user, logout, userPermissions } = useAuthStore()
  const navigate = useNavigate()
  const { t } = useTranslation('nav')
  const [section, setSection] = useState<NavSection>('system')

  const filteredTabs = useMemo(() => filterNavigationTabs(navigationTabs, userPermissions), [userPermissions])

  const hasSystemItems = filteredTabs.system.length > 0
  const hasUsersItems = filteredTabs.users.length > 0

  const currentSection = useMemo(() => {
    if (section === 'system' && !hasSystemItems && hasUsersItems) {
      return 'users'
    }
    if (section === 'users' && !hasUsersItems && hasSystemItems) {
      return 'system'
    }
    return section
  }, [section, hasSystemItems, hasUsersItems])

  if (!user) return null

  const links = filteredTabs[currentSection].map(item => (
    <LinksGroup
      key={item.label}
      {...item}
      label={t(item.label as Parameters<typeof t>[0])}
      mainLink={item.link}
      links={item.links?.map(l => ({ ...l, label: t(l.label as Parameters<typeof t>[0]) }))}
    />
  ))

  const version = import.meta.env.VITE_VCS_REVISION ?? null

  return (
    <nav className={classes.navbar}>
      <Group gap="xs" mb="xs" justify="space-between">
        <Avatar size="md" radius="xl" color="blue" variant="filled">
          {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
        </Avatar>
        <Box flex={1}>
          <Text size="xs" c="dimmed" mb={4}>
            {user.email}
          </Text>
          <Text size="xs" c="dimmed" mb={4}>
            {user.role}
          </Text>
        </Box>
      </Group>

      <SegmentedControl
        value={currentSection}
        onChange={value => setSection(value as NavSection)}
        transitionTimingFunction="ease"
        fullWidth
        data={[
          ...(hasSystemItems ? [{ label: t('system'), value: 'system' }] : []),
          ...(hasUsersItems ? [{ label: t('users'), value: 'users' }] : []),
        ]}
      />

      <ScrollArea className={classes.links} scrollbars="y">
        <div className={classes.linksInner}>{links}</div>
      </ScrollArea>

      <div className={classes.footer}>
        <UnstyledButton
          className={linkClasses.control}
          onClick={event => {
            event.preventDefault()
            logout()
            navigate({ to: '/login' })
          }}
        >
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            <ThemeIcon variant="light" size={30} className={linkClasses.icon}>
              <TbLogout2 size={18} />
            </ThemeIcon>
            <Box ml="md">{t('logout')}</Box>
          </Box>
        </UnstyledButton>

        <Box flex={1}>
          <Space h="xl" />
          <Text size="xs" c="dimmed" ta="center" mt="auto">
            {version}
          </Text>
        </Box>
      </div>
    </nav>
  )
}
